import { useState } from 'react';
import { useFirebase } from './FirebaseContext';

export const emptyProgress = {
  completedCourses: 0, totalCourses: 0, clinicalHours: 0, requiredClinicalHours: 0,
  researchProjects: 0, requiredResearchProjects: 0,
  deadlines: [], tasks: [], notes: [], shortTermGoals: [], longTermGoals: [], calendarEvents: []
};
export const progressKey = uid => `path2practice:progress:v1:${uid}`;

// Accept only this tracker's known shape, never shared legacy profiles or login flags.
function decode(raw) {
  if (raw === null) return { ...emptyProgress };
  const value = JSON.parse(raw);
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid progress');
  const data = { ...emptyProgress };
  for (const [key, fallback] of Object.entries(emptyProgress)) {
    if (!(key in value)) continue;
    if (Array.isArray(fallback)) {
      if (!Array.isArray(value[key]) || value[key].some(item => !item || typeof item !== 'object' || Array.isArray(item))) throw new Error('Invalid list');
      for (const item of value[key]) {
        if (!['string', 'number'].includes(typeof item.id)) throw new Error('Invalid record');
        const text = key === 'notes' ? 'content' : 'title';
        if (typeof item[text] !== 'string') throw new Error('Invalid text');
        if (item.steps !== undefined && (!Array.isArray(item.steps) || item.steps.some(step => !step || typeof step.title !== 'string'))) throw new Error('Invalid goal');
      }
    } else if (typeof value[key] !== 'number' || !Number.isFinite(value[key]) || value[key] < 0) throw new Error('Invalid total');
    data[key] = value[key];
  }
  return data;
}

// The consuming screen is keyed by UID, so neither data nor unsaved forms survive account changes.
export default function useLocalProgress(uid) {
  const { auth } = useFirebase();
  const read = () => {
    try {
      if (!uid || auth.currentUser?.uid !== uid) throw new Error('Session changed');
      return { data: decode(localStorage.getItem(progressKey(uid))), error: '', readFailed: false };
    } catch {
      return { data: { ...emptyProgress }, error: 'Your saved progress could not be loaded. Existing data has not been changed. Retry before editing.', readFailed: true };
    }
  };
  const [state, setState] = useState(read);
  const save = changes => {
    if (state.readFailed) return false;
    try {
      if (!uid || auth.currentUser?.uid !== uid) throw new Error('Session changed');
      const data = { ...state.data, ...changes };
      localStorage.setItem(progressKey(uid), JSON.stringify(data));
      setState({ data, error: '', readFailed: false });
      return true;
    } catch {
      setState(previous => ({ ...previous, error: 'Progress was not saved. Browser storage may be full or unavailable. Your previous saved data is unchanged.' }));
      return false;
    }
  };
  return { ...state, save, retry: () => setState(read()) };
}
